/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export type ScrollAxis = 'vertical' | 'horizontal' | 'both'

type ScrollContainerProps = {
  children: React.ReactNode
  /** Which axis scrolls. Default: 'vertical'. */
  axis?: ScrollAxis
  /** Track thickness in px. Default: 6. */
  size?: number
  /**
   * Drive a scroller that a child already owns, instead of scrolling here.
   *
   * Some components must keep their own scroll element — antd's `<Table
   * scroll={{ x }}` scrolls `.ant-table-content` itself, and that is what
   * makes `fixed` columns stick. Wrapping such a table in a second scroller
   * breaks the sticky columns. Pass the child scroller's selector and the
   * thumb reads from and writes to *that* element; this container then only
   * hosts the track and does not scroll.
   */
  targetSelector?: string
  /** Extra emotion styles for the viewport (sizing, padding, …). */
  className?: string
}

/** Minimum thumb length so it stays grabbable on very long content. */
const MIN_THUMB_PX = 32

/** Inset at each end of the track, so it does not run flush to the edges. */
const TRACK_INSET_PX = 8

/** Default track thickness. Slim enough to read as chrome, not as content. */
const DEFAULT_SIZE_PX = 6

/** Gap between the scrolled content and the track, so they never crowd. */
const TRACK_GAP_PX = 8

type Metrics = {
  /** Thumb length as a fraction of the track (0–1). */
  ratio: number
  /** Thumb offset as a fraction of the track (0–1). */
  offset: number
  /** Whether this axis currently overflows. */
  overflows: boolean
}

const EMPTY_METRICS: Metrics = { ratio: 0, offset: 0, overflows: false }

/**
 * Scroll area with a **custom-drawn** scrollbar that is always visible.
 *
 * Native scrollbars cannot be relied on here: macOS defaults to "Show scroll
 * bars: When scrolling", which hides them entirely at rest, and
 * `::-webkit-scrollbar` styling is ignored under some OS/browser combinations
 * (and unsupported in Firefox, which only offers `scrollbar-width`). So the
 * native bar is hidden outright and the thumb below is a real DOM element,
 * positioned from `scrollTop`/`scrollHeight`. That renders identically on every
 * browser and OS setting.
 *
 * The thumb is draggable, and the track is click-to-page. Reusable — not
 * feature-specific. Lives in `src/components` rather than `common/` because it
 * imports React, and `common/` is framework-agnostic.
 *
 * @example
 * <ScrollContainer axis='both' css={css`max-height: 400px;`}>
 *   <WideTable />
 * </ScrollContainer>
 *
 * @example Driving a scroller the child owns (keeps antd's fixed columns):
 * <ScrollContainer axis='horizontal' targetSelector='.ant-table-content'>
 *   <Table scroll={{ x: 'max-content' }} />
 * </ScrollContainer>
 */
export const ScrollContainer = React.forwardRef<
  HTMLDivElement,
  ScrollContainerProps
>(
  (
    {
      children,
      axis = 'vertical',
      size = DEFAULT_SIZE_PX,
      targetSelector,
      className,
    },
    forwardedRef
  ) => {
    const viewportRef = useRef<HTMLDivElement>(null)

    /** The element that actually scrolls: our viewport, or the child's. */
    const getScroller = useCallback((): HTMLElement | null => {
      const viewport = viewportRef.current
      if (!viewport) {
        return null
      }
      return targetSelector
        ? viewport.querySelector<HTMLElement>(targetSelector)
        : viewport
    }, [targetSelector])

    useImperativeHandle(
      forwardedRef,
      () => viewportRef.current as HTMLDivElement
    )

    const [vertical, setVertical] = useState<Metrics>(EMPTY_METRICS)
    const [horizontal, setHorizontal] = useState<Metrics>(EMPTY_METRICS)

    const showY = axis === 'vertical' || axis === 'both'
    const showX = axis === 'horizontal' || axis === 'both'

    const measure = useCallback(() => {
      const el = getScroller()
      if (!el) {
        return
      }

      const { scrollTop, scrollHeight, clientHeight } = el
      const { scrollLeft, scrollWidth, clientWidth } = el

      setVertical(
        scrollHeight > clientHeight
          ? {
              ratio: clientHeight / scrollHeight,
              offset: scrollTop / scrollHeight,
              overflows: true,
            }
          : EMPTY_METRICS
      )

      setHorizontal(
        scrollWidth > clientWidth
          ? {
              ratio: clientWidth / scrollWidth,
              offset: scrollLeft / scrollWidth,
              overflows: true,
            }
          : EMPTY_METRICS
      )
    }, [getScroller])

    // Measure on mount and whenever the content or viewport resizes. A plain
    // effect would miss content that grows later (async fields, expanded rows).
    useLayoutEffect(() => {
      const viewport = viewportRef.current
      if (!viewport) {
        return
      }
      measure()

      if (typeof ResizeObserver === 'undefined') {
        return
      }
      const observer = new ResizeObserver(measure)
      observer.observe(viewport)
      // Observe the content too — the viewport itself may never change size.
      Array.from(viewport.children).forEach((child) => observer.observe(child))

      // With a child scroller, the width that overflows lives deeper still
      // (antd's inner <table>), so observe the scroller and its content too.
      const scroller = getScroller()
      if (scroller && scroller !== viewport) {
        observer.observe(scroller)
        Array.from(scroller.children).forEach((child) =>
          observer.observe(child)
        )
      }
      return () => observer.disconnect()
    }, [measure, getScroller, children])

    // A child-owned scroller emits scroll on itself, not on our viewport, and
    // React's onScroll would not see it (scroll does not bubble). Subscribe
    // directly instead.
    useEffect(() => {
      const scroller = getScroller()
      if (!scroller || scroller === viewportRef.current) {
        return
      }
      scroller.addEventListener('scroll', measure, { passive: true })
      return () => scroller.removeEventListener('scroll', measure)
    }, [getScroller, measure, children])

    /** Drag the thumb; maps pointer delta back onto scrollTop/scrollLeft. */
    const startDrag = useCallback(
      (event: React.PointerEvent, isVertical: boolean) => {
        event.preventDefault()
        const el = getScroller()
        if (!el) {
          return
        }

        const startPos = isVertical ? event.clientY : event.clientX
        const startScroll = isVertical ? el.scrollTop : el.scrollLeft
        const trackPx = isVertical ? el.clientHeight : el.clientWidth
        const contentPx = isVertical ? el.scrollHeight : el.scrollWidth

        const onMove = (moveEvent: PointerEvent) => {
          const delta =
            (isVertical ? moveEvent.clientY : moveEvent.clientX) - startPos
          // Thumb travel : content travel is trackPx : contentPx
          const scrolled = startScroll + (delta * contentPx) / trackPx
          if (isVertical) {
            el.scrollTop = scrolled
          } else {
            el.scrollLeft = scrolled
          }
        }

        const onUp = () => {
          window.removeEventListener('pointermove', onMove)
          window.removeEventListener('pointerup', onUp)
        }

        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
      },
      [getScroller]
    )

    /** Click on empty track = page up/down (matches native behaviour). */
    const handleTrackClick = useCallback(
      (event: React.MouseEvent, isVertical: boolean) => {
        const el = getScroller()
        if (!el || event.target !== event.currentTarget) {
          return
        }
        const rect = event.currentTarget.getBoundingClientRect()
        const clickPos = isVertical
          ? event.clientY - rect.top
          : event.clientX - rect.left
        const thumbStart =
          (isVertical ? vertical.offset : horizontal.offset) *
          (isVertical ? rect.height : rect.width)
        const page = isVertical ? el.clientHeight : el.clientWidth
        const forward = clickPos > thumbStart

        if (isVertical) {
          el.scrollTop += forward ? page : -page
        } else {
          el.scrollLeft += forward ? page : -page
        }
      },
      [getScroller, vertical.offset, horizontal.offset]
    )

    const ownsScroll = !targetSelector

    return (
      <div css={rootStyle} className={className}>
        <div
          ref={viewportRef}
          css={viewportStyle(showY, showX, size, ownsScroll)}
          onScroll={ownsScroll ? measure : undefined}
        >
          {children}
        </div>

        {showY && vertical.overflows ? (
          <div
            css={verticalTrackStyle(size)}
            onMouseDown={(e) => handleTrackClick(e, true)}
          >
            <div
              css={verticalThumbStyle(vertical, size)}
              onPointerDown={(e) => startDrag(e, true)}
            />
          </div>
        ) : null}

        {showX && horizontal.overflows ? (
          <div
            css={horizontalTrackStyle(size)}
            onMouseDown={(e) => handleTrackClick(e, false)}
          >
            <div
              css={horizontalThumbStyle(horizontal, size)}
              onPointerDown={(e) => startDrag(e, false)}
            />
          </div>
        ) : null}
      </div>
    )
  }
)

ScrollContainer.displayName = 'ScrollContainer'

export default ScrollContainer

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const TRACK_COLOR = 'rgba(255, 255, 255, 0.06)'
const THUMB_COLOR = 'rgba(255, 255, 255, 0.30)'
const THUMB_HOVER_COLOR = 'rgba(255, 255, 255, 0.50)'

const rootStyle = css`
  position: relative;
  display: flex;
  min-height: 0;
  min-width: 0;
`

const viewportStyle = (
  showY: boolean,
  showX: boolean,
  size: number,
  ownsScroll: boolean
) => css`
  flex: 1;
  min-height: 0;
  min-width: 0;

  ${ownsScroll
    ? `
      overflow-y: ${showY ? 'auto' : 'hidden'};
      overflow-x: ${showX ? 'auto' : 'hidden'};
      /* Leave room for the custom track so content never sits under it */
      padding-right: ${showY ? size + TRACK_GAP_PX : 0}px;
      padding-bottom: ${showX ? size + TRACK_GAP_PX : 0}px;
    `
    : `
      /* A child owns the scroller; only reserve room for the track. */
      margin-bottom: ${showX ? size + TRACK_GAP_PX : 0}px;
      margin-right: ${showY ? size + TRACK_GAP_PX : 0}px;
    `}

  /*
   * Hide the NATIVE scrollbar on every engine — the custom thumb replaces it.
   * Applied to descendants too, so a child-owned scroller is covered as well.
   */
  &,
  & * {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* legacy Edge */
  }
  &::-webkit-scrollbar,
  & *::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  /* Keep a scrolled-to field clear of the container's top edge */
  scroll-padding: 12px;
`

const verticalTrackStyle = (size: number) => css`
  position: absolute;
  top: ${TRACK_INSET_PX}px;
  right: 0;
  bottom: ${TRACK_INSET_PX}px;
  width: ${size}px;
  background-color: ${TRACK_COLOR};
  border-radius: ${size}px;
  cursor: pointer;
`

const verticalThumbStyle = (metrics: Metrics, size: number) => css`
  position: absolute;
  left: 0;
  width: ${size}px;
  height: max(${MIN_THUMB_PX}px, ${metrics.ratio * 100}%);
  top: ${metrics.offset * 100}%;
  background-color: ${THUMB_COLOR};
  border-radius: ${size}px;
  cursor: grab;
  transition: background-color 0.15s;
  &:hover {
    background-color: ${THUMB_HOVER_COLOR};
  }
  &:active {
    background-color: ${THUMB_HOVER_COLOR};
    cursor: grabbing;
  }
`

const horizontalTrackStyle = (size: number) => css`
  position: absolute;
  left: ${TRACK_INSET_PX}px;
  right: ${TRACK_INSET_PX}px;
  bottom: 0;
  height: ${size}px;
  background-color: ${TRACK_COLOR};
  border-radius: ${size}px;
  cursor: pointer;
`

const horizontalThumbStyle = (metrics: Metrics, size: number) => css`
  position: absolute;
  top: 0;
  height: ${size}px;
  width: max(${MIN_THUMB_PX}px, ${metrics.ratio * 100}%);
  left: ${metrics.offset * 100}%;
  background-color: ${THUMB_COLOR};
  border-radius: ${size}px;
  cursor: grab;
  transition: background-color 0.15s;
  &:hover {
    background-color: ${THUMB_HOVER_COLOR};
  }
  &:active {
    background-color: ${THUMB_HOVER_COLOR};
    cursor: grabbing;
  }
`
